import type { Key, ReactNode } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import { TbArrowLeft, TbArrowRight } from 'react-icons/tb';

import { twMerge } from 'tailwind-merge';

import Skeleton from 'components/skeleton/skeleton';
import SortArrow from 'components/sort-arrow/sort-arrow';

import type { CheckboxProps } from 'components-new/checkbox/checkbox';
import Typography from 'components-new/typography/typography';
import Checkbox from 'components-new/checkbox/checkbox';
import Icon from 'components-new/icon/icon';
import type { EmptyStateProps } from 'components-new/empty-state/empty-state';
import EmptyState from 'components-new/empty-state/empty-state';

export type RowType = Record<string, unknown>;

export interface TableField {
  name: string;
  title: string;
  sortable?: boolean;
  render?: (value: unknown, row: RowType) => JSX.Element;
  dataClassName?: string;
  headerClassName?: string;
}

type TableProps = {
  disableFooter?: boolean;
  fields: Array<TableField>;
  data: Array<RowType>;
  pageSizeOptions?: Array<number>;
  currentPageSize?: number;
  totalPages: number;
  currentPage: number;
  selectable?: boolean;
  onSelectedItemsChange?: (rows: Array<RowType>) => void;
  onPageChange?: (page: number) => void;
  onNumberOfItemsPerPageChange?: (numberOfItemsPerPage: number) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isLoading?: boolean;
  containerClassName?: string;
  emptyStateProps?: EmptyStateProps;
};

const Table: React.FC<TableProps> = (props) => {
  const {
    disableFooter,
    fields,
    data,
    pageSizeOptions,
    currentPageSize,
    totalPages,
    currentPage,
    selectable = true,
    onSelectedItemsChange,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    containerClassName,
    emptyStateProps,
  } = props;

  const paginationPages = useMemo(() => {
    return [
      currentPage - 3,
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
      currentPage + 3,
    ]
      .filter((item) => item > 0)
      .filter((item) => item <= totalPages);
  }, [currentPage, totalPages]);

  const [isRowSelected, setIsRowSelected] = useState<Array<boolean>>(
    Array(currentPageSize).fill(false),
  );

  const updateSelectedRows = (newValues: Array<boolean>) => {
    setIsRowSelected([...newValues]);
  };

  const [headerCheckboxVariant, setHeaderCheckboxVariant] =
    useState<CheckboxProps['variant']>('unchecked');

  const updateHeaderCheckboxVariant = () => {
    const selectedRowsCounter = isRowSelected.reduce((acc, isSelected) => {
      return isSelected ? acc + 1 : acc;
    }, 0);

    const numberOfRows = currentPageSize ?? 0;

    if (selectedRowsCounter === 0) {
      setHeaderCheckboxVariant('unchecked');
    } else if (selectedRowsCounter < numberOfRows) {
      setHeaderCheckboxVariant('intermediate');
    } else {
      setHeaderCheckboxVariant('checked');
    }
  };

  const onHeaderCheckboxClicked = () => {
    let updatedHeaderCheckboxVariant: CheckboxProps['variant'];
    const updateIsRowSelected: Array<boolean> = Array(isRowSelected.length);

    switch (headerCheckboxVariant) {
      case 'checked':
      case 'intermediate': {
        updatedHeaderCheckboxVariant = 'unchecked';
        break;
      }
      case 'unchecked': {
        updatedHeaderCheckboxVariant = 'checked';
        break;
      }
      default: {
        updatedHeaderCheckboxVariant = 'disabled';
        break;
      }
    }

    setHeaderCheckboxVariant(updatedHeaderCheckboxVariant);

    if (updatedHeaderCheckboxVariant === 'checked')
      updateSelectedRows([...updateIsRowSelected.fill(true)]);
    else updateSelectedRows([...updateIsRowSelected.fill(false)]);
  };

  const onRowCheckboxClicked = (rowIndex: number) => {
    const updatedIsRowSelected = [...isRowSelected];

    updatedIsRowSelected[rowIndex] = !updatedIsRowSelected[rowIndex];

    updateSelectedRows(updatedIsRowSelected);
  };

  useEffect(updateHeaderCheckboxVariant, [isRowSelected, currentPageSize]);

  const resetSelectedItems = () => {
    setIsRowSelected(Array(currentPageSize).fill(false));
  };
  useEffect(resetSelectedItems, [currentPageSize, data]);

  const onNumberOfItemsPerPageChange = (numberOfItemsPerPage: number) => {
    props.onNumberOfItemsPerPageChange?.(numberOfItemsPerPage);
  };
  const onPageChange = (page: number) => {
    props.onPageChange?.(page);
  };

  const onNextPageClicked = () => {
    if (hasNextPage) onPageChange?.(currentPage + 1);
  };

  const onPreviousPageClicked = () => {
    if (hasPreviousPage) onPageChange?.(currentPage - 1);
  };

  useEffect(() => {
    const selectedRows = data.filter((_, index) => {
      return isRowSelected[index];
    });

    onSelectedItemsChange?.(selectedRows);
  }, [isRowSelected, data, onSelectedItemsChange]);

  const shouldShowFooter = !disableFooter && !isLoading && data.length !== 0;

  const containerClassNames = twMerge(
    'border rounded-xl border-[#E7E6E8] shadow bg-white w-full md:mr-0 overflow-x-scroll',
    containerClassName,
  );

  return (
    <div className={containerClassNames}>
      <table className="w-full table-auto overflow-x-scroll">
        <thead className="bg-white border-b border-[#E7E6E8] py-1">
          <tr>
            {selectable && (
              <th className="px-1">
                <Checkbox
                  variant={headerCheckboxVariant}
                  onClick={onHeaderCheckboxClicked}
                />
              </th>
            )}
            {fields.map((item) => (
              <Typography
                key={item.name}
                as="th"
                variant="heading-5"
                className={twMerge(
                  'text-left px-1 text-gray-400',
                  item.headerClassName,
                )}
              >
                <div className="flex flex-row items-center gap-x-1">
                  <p>{item.title}</p>
                  {item.sortable && <SortArrow state="down" size={8} />}
                </div>
              </Typography>
            ))}
          </tr>
        </thead>
        <tbody>
          {!isLoading &&
            data.map((item, index) => (
              <tr
                key={item.id as Key}
                className="bg-white border-b border-[#E7E6E8]"
              >
                {selectable && (
                  <th className="py-2 px-1">
                    <Checkbox
                      variant={isRowSelected[index] ? 'checked' : 'unchecked'}
                      onClick={() => onRowCheckboxClicked(index)}
                    />
                  </th>
                )}
                {fields.map((field) =>
                  field.render ? (
                    <td
                      className={twMerge(
                        'text-left py-2 px-1',
                        field.dataClassName,
                      )}
                      key={field.title}
                    >
                      {field.render(item[field.name], item)}
                    </td>
                  ) : (
                    <Typography
                      key={field.name}
                      as="td"
                      variant="body-large"
                      className={twMerge(
                        'text-left py-2 px-1',
                        field.dataClassName,
                      )}
                    >
                      {item[field.name] as ReactNode}
                    </Typography>
                  ),
                )}
              </tr>
            ))}
          {isLoading &&
            [...Array.from({ length: 10 }, (x, i) => i)].map((key: number) => (
              <tr
                key={key}
                className="bg-white border-b border-[#E7E6E8] leading-10"
              >
                <th>
                  <Checkbox variant="disabled" />
                </th>
                {fields.map((field) => (
                  <td className="text-left  py-2 pr-8" key={field.title}>
                    <Skeleton className="w-20" />
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
        {shouldShowFooter && (
          <tr className="mt-auto">
            <td colSpan={fields.length + Number(!!selectable)}>
              <div className="bg-white relative p-4 w-full flex flex-row justify-between items-center h-6">
                {currentPage && totalPages && (
                  <Typography
                    as="p"
                    variant="ui-nav-sm"
                    className="text-gray-400 hidden sm:block px-2"
                  >
                    {`PAGE ${currentPage} OUT OF ${totalPages}`}
                  </Typography>
                )}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-x-5 text-gray-400">
                  <button
                    type="button"
                    onClick={onPreviousPageClicked}
                    disabled={!hasPreviousPage}
                    className={twMerge(
                      'flex',
                      !hasPreviousPage && 'cursor-not-allowed',
                    )}
                  >
                    <Icon
                      icon={TbArrowLeft}
                      size={16}
                      className={twMerge(
                        hasPreviousPage ? 'text-gray-400' : 'text-gray-200',
                      )}
                    />
                  </button>
                  {paginationPages?.map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => onPageChange(page)}
                    >
                      <Typography
                        as="p"
                        variant="ui-nav-sm"
                        className={
                          currentPage === page
                            ? 'text-primary'
                            : 'text-gray-400'
                        }
                      >
                        {page}
                      </Typography>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={onNextPageClicked}
                    disabled={!hasNextPage}
                    className={twMerge(
                      'flex',
                      !hasNextPage && 'cursor-not-allowed',
                    )}
                  >
                    <Icon
                      icon={TbArrowRight}
                      size={16}
                      className={twMerge(
                        hasNextPage ? 'text-gray-400' : 'text-gray-200',
                      )}
                    />
                  </button>
                </div>
                <div className="hidden sm:flex items-center gap-x-2 px-4">
                  <Typography
                    as="p"
                    variant="ui-nav-sm"
                    className="text-gray-400 mr-4"
                  >
                    ITEMS PER PAGE
                  </Typography>
                  {pageSizeOptions?.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => onNumberOfItemsPerPageChange(option)}
                    >
                      <Typography
                        as="p"
                        variant="ui-nav-sm"
                        className={twMerge(
                          option === currentPageSize
                            ? 'text-primary'
                            : 'text-gray-400',
                        )}
                      >
                        {option}
                      </Typography>
                    </button>
                  ))}
                </div>
              </div>
            </td>
          </tr>
        )}
      </table>
      {data.length === 0 && !isLoading && (
        <div className="w-full flex justify-center items-center py-3">
          <EmptyState {...emptyStateProps} />
        </div>
      )}
    </div>
  );
};

export default Table;
